export type DaySchedule = {
  day: number;
  closed: boolean;
  open: string;
  close: string;
};

export type StoreHoursConfig = {
  timezone: string;
  accepting_orders: boolean;
  days: DaySchedule[];
};

export const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export function defaultStoreHours(): StoreHoursConfig {
  return {
    timezone: 'Asia/Amman',
    accepting_orders: true,
    days: Array.from({ length: 7 }, (_, day) => ({
      day,
      closed: false,
      open: '08:00',
      close: '22:00',
    })),
  };
}

export function normalizeStoreHours(raw: unknown): StoreHoursConfig {
  if (!raw || typeof raw !== 'object') return defaultStoreHours();
  const parsed = raw as Partial<StoreHoursConfig>;
  const base = defaultStoreHours();
  const byDay = new Map(
    Array.isArray(parsed.days) ? parsed.days.map((d) => [Number(d.day), d]) : []
  );
  const days = Array.from({ length: 7 }, (_, day) => {
    const incoming = byDay.get(day);
    if (!incoming) return base.days[day];
    return {
      day,
      closed: Boolean(incoming.closed),
      open: incoming.open || base.days[day].open,
      close: incoming.close || base.days[day].close,
    };
  });
  return {
    timezone: parsed.timezone || base.timezone,
    accepting_orders: parsed.accepting_orders !== false,
    days,
  };
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function dayKey(d: Date): string {
  return startOfDay(d).toISOString().slice(0, 10);
}

export type OnlineRateMetrics = {
  scheduledOpenDays: number;
  activeOpenDays: number;
  onlineRatePct: number;
  hasSchedule: boolean;
};

export function computeOnlineRateMetrics(
  start: Date,
  end: Date,
  storeHours: StoreHoursConfig | null | undefined,
  completedOrderDates: Date[]
): OnlineRateMetrics {
  const hours = normalizeStoreHours(storeHours);
  const scheduleByDay = new Map(hours.days.map((d) => [d.day, d]));

  const orderDays = new Set(completedOrderDates.map(dayKey));

  let scheduledOpenDays = 0;
  let activeOpenDays = 0;

  const cursor = startOfDay(start);
  const endDay = startOfDay(end);

  while (cursor <= endDay) {
    const weekday = cursor.getDay();
    const row = scheduleByDay.get(weekday);
    const isScheduledOpen = row && !row.closed;

    if (isScheduledOpen) {
      scheduledOpenDays += 1;
      if (orderDays.has(dayKey(cursor))) {
        activeOpenDays += 1;
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  const onlineRatePct =
    scheduledOpenDays > 0 ? Math.round((activeOpenDays / scheduledOpenDays) * 100) : 0;

  return {
    scheduledOpenDays,
    activeOpenDays,
    onlineRatePct,
    hasSchedule: scheduledOpenDays > 0,
  };
}

function parseTimeToMinutes(hhmm: string): number | null {
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/** Close at or before open on the clock → spans past midnight into the next day. */
export function isOvernightClose(open: string, close: string): boolean {
  const openM = parseTimeToMinutes(open);
  const closeM = parseTimeToMinutes(close);
  if (openM == null || closeM == null) return false;
  return closeM <= openM;
}

export function formatDayHours(row: DaySchedule): string {
  if (row.closed) return 'Closed';
  if (isOvernightClose(row.open, row.close)) {
    return `${row.open} – ${row.close} (next day)`;
  }
  return `${row.open} – ${row.close}`;
}

/** Normalize browser time input (HH:MM or HH:MM:SS) to HH:MM for the API. */
export function normalizeTimeInput(value: string): string {
  const m = String(value || '').trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return value;
  return `${String(Number(m[1])).padStart(2, '0')}:${m[2]}`;
}

export function isValidTimeInput(value: string): boolean {
  return /^\d{2}:\d{2}$/.test(normalizeTimeInput(value));
}

export function splitTimeInput(value: string): { hour: string; minute: string } {
  const normalized = normalizeTimeInput(value);
  const m = normalized.match(/^(\d{2}):(\d{2})$/);
  if (!m) return { hour: '08', minute: '00' };
  return { hour: m[1], minute: m[2] };
}

export function joinTimeInput(hour: string, minute: string): string {
  return `${String(Number(hour)).padStart(2, '0')}:${minute.padStart(2, '0')}`;
}

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

export const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

export const MINUTE_OPTIONS_ALL = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

export function normalizeHourPart(value: string): string {
  const trimmed = String(value || '').trim();
  if (!/^\d{1,2}$/.test(trimmed)) return trimmed;
  const n = Number(trimmed);
  if (n < 0 || n > 23) return trimmed;
  return String(n).padStart(2, '0');
}

export function normalizeMinutePart(value: string): string {
  const trimmed = String(value || '').trim();
  if (!/^\d{1,2}$/.test(trimmed)) return trimmed;
  const n = Number(trimmed);
  if (n < 0 || n > 59) return trimmed;
  return String(n).padStart(2, '0');
}

export function isValidHourPart(value: string): boolean {
  return /^\d{2}$/.test(normalizeHourPart(value));
}

export function isValidMinutePart(value: string): boolean {
  return /^\d{2}$/.test(normalizeMinutePart(value));
}

export function minuteOptionsIncluding(current: string): string[] {
  const norm = normalizeMinutePart(current);
  if (isValidMinutePart(norm) && !MINUTE_OPTIONS_ALL.includes(norm)) {
    return [...MINUTE_OPTIONS_ALL, norm].sort();
  }
  return MINUTE_OPTIONS_ALL;
}

export function filterNumericOptions(options: string[], query: string): string[] {
  const q = query.trim();
  if (!q) return options;
  return options.filter((o) => o.startsWith(q) || o.includes(q));
}

export function normalizeStoreHoursForSave(config: StoreHoursConfig): StoreHoursConfig {
  return {
    ...config,
    days: config.days.map((row) => ({
      ...row,
      open: normalizeTimeInput(row.open),
      close: normalizeTimeInput(row.close),
    })),
  };
}

function getAmmanMinutesOfDay(date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Amman',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return hour * 60 + minute;
}

function getAmmanWeekday(date = new Date()): number {
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Amman',
    weekday: 'short',
  }).format(date);
  const map: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return map[weekday] ?? date.getDay();
}

function isWithinOpenWindow(open: string, close: string, now = new Date()): boolean {
  const nowM = getAmmanMinutesOfDay(now);
  const openM = parseTimeToMinutes(open);
  const closeM = parseTimeToMinutes(close);
  if (openM == null || closeM == null) return true;
  if (openM < closeM) return nowM >= openM && nowM <= closeM;
  if (openM > closeM) return nowM >= openM || nowM <= closeM;
  return true;
}

export function storeHoursFromCafe(cafe: {
  store_hours?: unknown;
  accepting_orders?: boolean | number | null;
} | null | undefined): StoreHoursConfig {
  const config = normalizeStoreHours(cafe?.store_hours);
  if (cafe?.accepting_orders === 0 || cafe?.accepting_orders === false) {
    config.accepting_orders = false;
  }
  return config;
}

export function isCafeOpenNow(config: StoreHoursConfig | null | undefined, now = new Date()): boolean {
  const hours = normalizeStoreHours(config);
  if (!hours.accepting_orders) return false;

  const weekday = getAmmanWeekday(now);
  const day = hours.days.find((d) => d.day === weekday);
  if (!day || day.closed) return false;

  return isWithinOpenWindow(day.open, day.close, now);
}

export function getCafeClosedMessage(config: StoreHoursConfig | null | undefined, now = new Date()): string {
  const hours = normalizeStoreHours(config);
  if (!hours.accepting_orders) {
    return 'This café is temporarily not accepting orders.';
  }

  const weekday = getAmmanWeekday(now);
  const day = hours.days.find((d) => d.day === weekday);
  if (!day || day.closed) {
    return `Closed today (${DAY_LABELS[weekday]}).`;
  }

  if (!isWithinOpenWindow(day.open, day.close, now)) {
    return `Closed now. Today's hours: ${day.open} – ${day.close} (Jordan time).`;
  }

  return 'This café is not accepting orders right now.';
}

export function formatTodayHours(config: StoreHoursConfig | null | undefined, now = new Date()): string {
  const hours = normalizeStoreHours(config);
  const weekday = getAmmanWeekday(now);
  const day = hours.days.find((d) => d.day === weekday);
  if (!day) return 'Hours unavailable';
  if (day.closed) return `Closed today (${DAY_LABELS[weekday]})`;
  return `Today: ${day.open} – ${day.close} (Jordan time)`;
}
